import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XIcon, CheckIcon, InfoIcon, AlertCircleIcon } from 'lucide-react';
import { useBooking } from '../contexts/BookingContext';
interface FormData {
  titel: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  email: string;
  telefon: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  praxis: string;
  position: string;
  teilnehmer: number;
  courses: string[];
  aufmerksam: string;
  ernaehrung: string;
  bemerkung: string;
  agbAccepted: boolean;
  anmeldungAccepted: boolean;
}
const INITIAL_DATA: FormData = {
  titel: '',
  vorname: '',
  nachname: '',
  geburtsdatum: '',
  email: '',
  telefon: '',
  strasse: '',
  hausnummer: '',
  plz: '',
  ort: '',
  praxis: '',
  position: '',
  teilnehmer: 1,
  courses: [],
  aufmerksam: '',
  ernaehrung: '',
  bemerkung: '',
  agbAccepted: false,
  anmeldungAccepted: false
};
const COURSES = [{
  id: 'intensiv',
  title: '2N Intensiv Curriculum',
  points: 78,
  price: 5490,
  isHighlight: true
}, {
  id: 'a-kurs',
  title: 'A-Kurs White Aesthetics',
  points: 15,
  price: 1650
}, {
  id: 'b-kurs',
  title: 'B-Kurs Red Aesthetics',
  points: 16,
  price: 1650
}, {
  id: 'c-kurs',
  title: 'C-Kurs Funktion',
  points: 16,
  price: 1650
}, {
  id: 'd-kurs',
  title: 'D-Kurs Champions League',
  points: 15,
  price: 1650
}, {
  id: 'aligner',
  title: 'Alignertherapie',
  points: 16,
  price: 1650
}, {
  id: 'female-boss',
  title: 'Female Boss',
  points: 7,
  price: 890
}];
function formatPrice(cents: number) {
  return cents.toLocaleString('de-DE') + ' €';
}
export function BookingModal() {
  const {
    isOpen,
    closeBooking
  } = useBooking();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setTimeout(() => {
        setStep(1);
        setFormData(INITIAL_DATA);
        setErrors({});
        setIsSubmitted(false);
      }, 300);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const n = {
          ...prev
        };
        delete n[field];
        return n;
      });
    }
  };
  const toggleCourse = (courseId: string) => {
    setFormData(prev => {
      const isSelected = prev.courses.includes(courseId);
      const newCourses = isSelected ? prev.courses.filter(id => id !== courseId) : [...prev.courses, courseId];
      return {
        ...prev,
        courses: newCourses
      };
    });
    if (errors.courses) {
      setErrors(prev => {
        const n = {
          ...prev
        };
        delete n.courses;
        return n;
      });
    }
  };
  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.titel) newErrors.titel = 'Bitte wählen Sie einen Titel';
    if (!formData.vorname.trim()) newErrors.vorname = 'Bitte geben Sie Ihren Vornamen ein';
    if (!formData.nachname.trim()) newErrors.nachname = 'Bitte geben Sie Ihren Nachnamen ein';
    if (!formData.geburtsdatum) newErrors.geburtsdatum = 'Bitte geben Sie Ihr Geburtsdatum ein';
    if (!formData.email.trim()) newErrors.email = 'Bitte geben Sie Ihre E-Mail ein';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Bitte geben Sie eine gültige E-Mail ein';
    if (!formData.telefon.trim()) newErrors.telefon = 'Bitte geben Sie Ihre Telefonnummer ein';
    if (!formData.strasse.trim()) newErrors.strasse = 'Bitte geben Sie Ihre Straße ein';
    if (!formData.hausnummer.trim()) newErrors.hausnummer = 'Bitte geben Sie Ihre Hausnummer ein';
    if (!formData.plz.trim()) newErrors.plz = 'Bitte geben Sie Ihre PLZ ein';
    if (!formData.ort.trim()) newErrors.ort = 'Bitte geben Sie Ihren Ort ein';
    if (!formData.praxis.trim()) newErrors.praxis = 'Bitte geben Sie Ihre Praxis ein';
    if (!formData.position) newErrors.position = 'Bitte wählen Sie Ihre Position';
    if (formData.teilnehmer < 1 || formData.teilnehmer > 6) newErrors.teilnehmer = 'Bitte 1-6 Teilnehmer angeben';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (formData.courses.length === 0) newErrors.courses = 'Bitte wählen Sie mindestens einen Kurs';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(s => Math.min(s + 1, 3));
  };
  const handlePrev = () => setStep(s => Math.max(s - 1, 1));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      if (validateStep1()) setStep(2);
      return;
    }
    if (step === 2) {
      if (validateStep2()) setStep(3);
      return;
    }
    // Only submit on step 3
    console.log('Booking submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      closeBooking();
    }, 3000);
  };
  const isIntensivSelected = formData.courses.includes('intensiv');
  // Calculate total
  const selectedCourses = COURSES.filter(c => formData.courses.includes(c.id));
  const subtotal = selectedCourses.reduce((sum, c) => sum + c.price, 0);
  const total = subtotal * formData.teilnehmer;
  const totalMwst = Math.round(total * 1.19 * 100) / 100;
  const inputClass = (field: string) => `px-4 py-2.5 rounded-lg border ${errors[field] ? 'border-rose-400 ring-2 ring-rose-100' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-[#59d0d4] focus:border-transparent`;
  const selectClass = (field: string) => `px-4 py-2.5 rounded-lg border ${errors[field] ? 'border-rose-400 ring-2 ring-rose-100' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-[#59d0d4] focus:border-transparent bg-white`;
  return createPortal(<div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6" data-id="element-73">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fadeIn" onClick={closeBooking} data-id="element-74" />

      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-scaleIn flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()} data-id="element-75">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white z-10 flex-shrink-0" data-id="element-76">
          <h2 className="text-xl font-extrabold text-slate-800" data-id="element-77">Kursbuchung</h2>
          <button onClick={closeBooking} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-78">
            <XIcon className="w-5 h-5" data-id="element-79" />
          </button>
        </div>

        {isSubmitted ? <div className="p-12 text-center flex flex-col items-center justify-center flex-grow" data-id="element-80">
            <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-6" data-id="element-81">
              <CheckIcon className="w-10 h-10" data-id="element-82" />
            </div>
            <h3 className="text-2xl font-extrabold text-slate-800 mb-2" data-id="element-83">
              Vielen Dank für Ihre Anmeldung!
            </h3>
            <p className="text-slate-500 font-medium" data-id="element-84">
              Wir haben Ihre Daten erhalten und werden uns in Kürze bei Ihnen
              melden.
            </p>
          </div> : <>
            {/* Step Indicator */}
            <div className="px-4 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4 bg-slate-50/50 flex-shrink-0" data-id="element-85">
              <div className="flex items-center justify-between relative max-w-md mx-auto" data-id="element-86">
                <div className="absolute left-0 top-5 -translate-y-1/2 w-full h-0.5 bg-slate-200 z-0" data-id="element-87"></div>
                <div className="absolute left-0 top-5 -translate-y-1/2 h-0.5 bg-[#3b7780] z-0 transition-all duration-500" style={{
              width: `${(step - 1) / 2 * 100}%`
            }} data-id="element-88"></div>
                {[{
              num: 1,
              label: 'Persönliche Daten'
            }, {
              num: 2,
              label: 'Kursauswahl'
            }, {
              num: 3,
              label: 'Übersicht & Absenden'
            }].map(s => <div key={s.num} className="relative z-10 flex flex-col items-center gap-2" data-id="element-89">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors duration-300 ${step >= s.num ? 'bg-[#3b7780] text-white shadow-md' : 'bg-white border-2 border-slate-200 text-slate-400'}`} data-id="element-90">
                      {step > s.num ? <CheckIcon className="w-5 h-5" data-id="element-91" /> : s.num}
                    </div>
                    <span className={`text-xs font-bold hidden sm:block ${step >= s.num ? 'text-[#3b7780]' : 'text-slate-400'}`} data-id="element-92">
                      {s.label}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Form Content */}
            <div className="flex-grow overflow-y-auto p-6 sm:p-8" data-id="element-93">
              <div data-id="element-94">
                {/* STEP 1 */}
                <div className={step === 1 ? 'space-y-5 animate-fadeIn' : 'hidden'} data-id="element-95">
                  <h3 className="text-2xl font-extrabold text-slate-800 mb-4" data-id="element-96">
                    Persönliche Daten
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5" data-id="element-97">
                    <div className="flex flex-col gap-1" data-id="element-98">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-99">
                        Titel <span className="text-rose-500" data-id="element-100">*</span>
                      </label>
                      <select value={formData.titel} onChange={e => updateField('titel', e.target.value)} className={selectClass('titel')} data-id="element-101">
                        <option value="" data-id="element-102">Bitte wählen</option>
                        <option value="Dr." data-id="element-103">Dr.</option>
                        <option value="Dr. med. dent." data-id="element-104">Dr. med. dent.</option>
                        <option value="Prof. Dr." data-id="element-105">Prof. Dr.</option>
                        <option value="ZA/ZÄ" data-id="element-106">ZA/ZÄ</option>
                      </select>
                      {errors.titel && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-107">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-108" />
                          {errors.titel}
                        </span>}
                    </div>
                    <div className="flex flex-col gap-1" data-id="element-109">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-110">
                        Vorname <span className="text-rose-500" data-id="element-111">*</span>
                      </label>
                      <input type="text" value={formData.vorname} onChange={e => updateField('vorname', e.target.value)} className={inputClass('vorname')} data-id="element-112" />
                      {errors.vorname && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-113">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-114" />
                          {errors.vorname}
                        </span>}
                    </div>
                    <div className="flex flex-col gap-1" data-id="element-115">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-116">
                        Nachname <span className="text-rose-500" data-id="element-117">*</span>
                      </label>
                      <input type="text" value={formData.nachname} onChange={e => updateField('nachname', e.target.value)} className={inputClass('nachname')} data-id="element-118" />
                      {errors.nachname && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-119">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-120" />
                          {errors.nachname}
                        </span>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5" data-id="element-121">
                    <div className="flex flex-col gap-1" data-id="element-122">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-123">
                        Geburtsdatum <span className="text-rose-500" data-id="element-124">*</span>
                      </label>
                      <input type="date" value={formData.geburtsdatum} onChange={e => updateField('geburtsdatum', e.target.value)} className={inputClass('geburtsdatum')} data-id="element-125" />
                      {errors.geburtsdatum && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-126">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-127" />
                          {errors.geburtsdatum}
                        </span>}
                    </div>
                    <div className="flex flex-col gap-1" data-id="element-128">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-129">
                        E-Mail <span className="text-rose-500" data-id="element-130">*</span>
                      </label>
                      <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className={inputClass('email')} data-id="element-131" />
                      {errors.email && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-132">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-133" />
                          {errors.email}
                        </span>}
                    </div>
                    <div className="flex flex-col gap-1" data-id="element-134">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-135">
                        Telefon <span className="text-rose-500" data-id="element-136">*</span>
                      </label>
                      <input type="tel" value={formData.telefon} onChange={e => updateField('telefon', e.target.value)} className={inputClass('telefon')} data-id="element-137" />
                      {errors.telefon && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-138">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-139" />
                          {errors.telefon}
                        </span>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5" data-id="element-140">
                    <div className="flex flex-col gap-1" data-id="element-141">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-142">
                        Straße <span className="text-rose-500" data-id="element-143">*</span>
                      </label>
                      <input type="text" value={formData.strasse} onChange={e => updateField('strasse', e.target.value)} className={inputClass('strasse')} data-id="element-144" />
                      {errors.strasse && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-145">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-146" />
                          {errors.strasse}
                        </span>}
                    </div>
                    <div className="flex flex-col gap-1" data-id="element-147">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-148">
                        Hausnummer <span className="text-rose-500" data-id="element-149">*</span>
                      </label>
                      <input type="text" value={formData.hausnummer} onChange={e => updateField('hausnummer', e.target.value)} className={inputClass('hausnummer')} data-id="element-150" />
                      {errors.hausnummer && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-151">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-152" />
                          {errors.hausnummer}
                        </span>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5" data-id="element-153">
                    <div className="flex flex-col gap-1" data-id="element-154">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-155">
                        PLZ <span className="text-rose-500" data-id="element-156">*</span>
                      </label>
                      <input type="text" value={formData.plz} onChange={e => updateField('plz', e.target.value)} className={inputClass('plz')} data-id="element-157" />
                      {errors.plz && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-158">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-159" />
                          {errors.plz}
                        </span>}
                    </div>
                    <div className="flex flex-col gap-1" data-id="element-160">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-161">
                        Ort <span className="text-rose-500" data-id="element-162">*</span>
                      </label>
                      <input type="text" value={formData.ort} onChange={e => updateField('ort', e.target.value)} className={inputClass('ort')} data-id="element-163" />
                      {errors.ort && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-164">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-165" />
                          {errors.ort}
                        </span>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5" data-id="element-166">
                    <div className="flex flex-col gap-1" data-id="element-167">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-168">
                        Praxis / Labor <span className="text-rose-500" data-id="element-169">*</span>
                      </label>
                      <input type="text" value={formData.praxis} onChange={e => updateField('praxis', e.target.value)} className={inputClass('praxis')} data-id="element-170" />
                      {errors.praxis && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-171">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-172" />
                          {errors.praxis}
                        </span>}
                    </div>
                    <div className="flex flex-col gap-1" data-id="element-173">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-174">
                        Meine Position <span className="text-rose-500" data-id="element-175">*</span>
                      </label>
                      <select value={formData.position} onChange={e => updateField('position', e.target.value)} className={selectClass('position')} data-id="element-176">
                        <option value="" data-id="element-177">Bitte wählen</option>
                        <option value="Praxisinhaber/in" data-id="element-178">
                          Praxisinhaber/in
                        </option>
                        <option value="Angestellte/r Zahnarzt/Zahnärztin" data-id="element-179">
                          Angestellte/r Zahnarzt/Zahnärztin
                        </option>
                        <option value="Assistenzzahnarzt/-ärztin" data-id="element-180">
                          Assistenzzahnarzt/-ärztin
                        </option>
                        <option value="Zahntechniker/in" data-id="element-181">
                          Zahntechniker/in
                        </option>
                        <option value="Sonstige" data-id="element-182">Sonstige</option>
                      </select>
                      {errors.position && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-183">
                          <AlertCircleIcon className="w-3 h-3" data-id="element-184" />
                          {errors.position}
                        </span>}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1" data-id="element-185">
                    <label className="text-sm font-semibold text-slate-700" data-id="element-186">
                      Anzahl Teilnehmer <span className="text-rose-500" data-id="element-187">*</span>
                    </label>
                    <input type="number" min="1" max="6" value={formData.teilnehmer} onChange={e => updateField('teilnehmer', parseInt(e.target.value) || 1)} className={inputClass('teilnehmer')} data-id="element-188" />
                    <span className="text-xs font-bold text-slate-500 mt-0.5" data-id="element-189">
                      Bitte geben Sie die Anzahl der Teilnehmer an (1-6)
                    </span>
                    {errors.teilnehmer && <span className="text-xs text-rose-500 font-medium flex items-center gap-1" data-id="element-190">
                        <AlertCircleIcon className="w-3 h-3" data-id="element-191" />
                        {errors.teilnehmer}
                      </span>}
                  </div>
                </div>

                {/* STEP 2 */}
                <div className={step === 2 ? 'space-y-5 animate-fadeIn' : 'hidden'} data-id="element-192">
                  <div data-id="element-193">
                    <h3 className="text-2xl font-extrabold text-slate-800 mb-2" data-id="element-194">
                      Wählen Sie Ihre Kurse
                    </h3>
                    <p className="text-slate-500 font-medium" data-id="element-195">
                      Sie können einzelne Kurse oder das gesamte Curriculum
                      buchen.
                    </p>
                  </div>

                  {errors.courses && <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-center gap-2" data-id="element-196">
                      <AlertCircleIcon className="w-4 h-4 text-rose-500 flex-shrink-0" data-id="element-197" />
                      <span className="text-sm font-medium text-rose-700" data-id="element-198">
                        {errors.courses}
                      </span>
                    </div>}

                  <div className="space-y-3" data-id="element-199">
                    {COURSES.map(course => <label key={course.id} className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.courses.includes(course.id) ? 'border-[#3b7780] bg-[#3b7780]/5' : course.isHighlight ? 'border-[#59d0d4]/50 hover:border-[#59d0d4] bg-white' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-200">
                        <div className="flex items-center h-6" data-id="element-201">
                          <input type="checkbox" checked={formData.courses.includes(course.id)} onChange={() => toggleCourse(course.id)} className="w-5 h-5 text-[#3b7780] rounded border-slate-300 focus:ring-[#59d0d4] focus:ring-2 cursor-pointer" data-id="element-202" />
                        </div>
                        <div className="ml-4 flex-1" data-id="element-203">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1" data-id="element-204">
                            <span className={`font-extrabold text-lg ${course.isHighlight ? 'text-[#3b7780]' : 'text-slate-700'}`} data-id="element-205">
                              {course.title}
                            </span>
                            <span className="font-bold text-[#3b7780]" data-id="element-206">
                              {formatPrice(course.price)}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-slate-500" data-id="element-207">
                            {course.points} Punkte
                          </span>
                          {course.isHighlight && <span className="text-xs font-bold text-[#59d0d4] block mt-1" data-id="element-208">
                              Bestes Preis-Leistungs-Verhältnis
                            </span>}
                        </div>
                      </label>)}
                  </div>

                  {isIntensivSelected && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 animate-fadeIn" data-id="element-209">
                      <InfoIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-210" />
                      <p className="text-sm font-medium text-amber-800" data-id="element-211">
                        <strong data-id="element-212">Hinweis:</strong> Das Intensiv Curriculum
                        beinhaltet bereits den A-Kurs, B-Kurs, C-Kurs, D-Kurs
                        sowie die Alignertherapie.
                      </p>
                    </div>}
                </div>

                {/* STEP 3 */}
                <div className={step === 3 ? 'space-y-6 animate-fadeIn' : 'hidden'} data-id="element-213">
                  <h3 className="text-2xl font-extrabold text-slate-800 mb-2" data-id="element-214">
                    Weitere Angaben & Übersicht
                  </h3>

                  <div className="space-y-4" data-id="element-215">
                    <div className="flex flex-col gap-1" data-id="element-216">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-217">
                        Wie sind Sie auf uns aufmerksam geworden?
                      </label>
                      <select value={formData.aufmerksam} onChange={e => updateField('aufmerksam', e.target.value)} className="px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#59d0d4] focus:border-transparent bg-white" data-id="element-218">
                        <option value="" data-id="element-219">Bitte wählen</option>
                        <option value="Google" data-id="element-220">Google</option>
                        <option value="Social Media" data-id="element-221">Social Media</option>
                        <option value="Empfehlung" data-id="element-222">Empfehlung</option>
                        <option value="Fachzeitschrift" data-id="element-223">Fachzeitschrift</option>
                        <option value="Kongress/Messe" data-id="element-224">Kongress/Messe</option>
                        <option value="Sonstiges" data-id="element-225">Sonstiges</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-1" data-id="element-226">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-227">
                        Ich ernähre mich
                      </label>
                      <select value={formData.ernaehrung} onChange={e => updateField('ernaehrung', e.target.value)} className="px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#59d0d4] focus:border-transparent bg-white" data-id="element-228">
                        <option value="Keine Präferenz" data-id="element-229">Keine Präferenz</option>
                        <option value="Vegan" data-id="element-230">Vegan</option>
                        <option value="Vegetarisch" data-id="element-231">Vegetarisch</option>
                        <option value="Fleisch/Fisch" data-id="element-232">Fleisch/Fisch</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-1" data-id="element-233">
                      <label className="text-sm font-semibold text-slate-700" data-id="element-234">
                        Bemerkung
                      </label>
                      <textarea rows={3} value={formData.bemerkung} onChange={e => updateField('bemerkung', e.target.value)} placeholder="Haben Sie weitere Anmerkungen oder Wünsche?" className="px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#59d0d4] focus:border-transparent resize-none" data-id="element-235"></textarea>
                    </div>
                  </div>

                  {/* Booking Summary */}
                  <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 mt-6" data-id="element-236">
                    <h4 className="text-lg font-extrabold text-slate-800 mb-4" data-id="element-237">
                      Ihre Buchungsübersicht
                    </h4>

                    <div className="space-y-3 mb-4" data-id="element-238">
                      {selectedCourses.map(course => <div key={course.id} className="flex items-center justify-between" data-id="element-239">
                          <div data-id="element-240">
                            <span className="font-bold text-slate-700" data-id="element-241">
                              {course.title}
                            </span>
                            <span className="text-xs text-slate-500 ml-2" data-id="element-242">
                              ({course.points} Punkte)
                            </span>
                          </div>
                          <span className="font-bold text-slate-700" data-id="element-243">
                            {formatPrice(course.price)}
                          </span>
                        </div>)}
                    </div>

                    {formData.teilnehmer > 1 && <div className="flex items-center justify-between text-sm text-slate-500 font-medium border-t border-slate-200 pt-3 mb-3" data-id="element-244">
                        <span data-id="element-245">× {formData.teilnehmer} Teilnehmer</span>
                        <span data-id="element-246">{formatPrice(total)}</span>
                      </div>}

                    <div className="border-t border-slate-200 pt-3 space-y-2" data-id="element-247">
                      <div className="flex items-center justify-between" data-id="element-248">
                        <span className="font-bold text-slate-600" data-id="element-249">Netto</span>
                        <span className="font-bold text-slate-700" data-id="element-250">
                          {formatPrice(total)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-slate-500" data-id="element-251">
                        <span data-id="element-252">zzgl. 19% MwSt.</span>
                        <span data-id="element-253">{formatPrice(Math.round(total * 0.19))}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-slate-300" data-id="element-254">
                        <span className="text-lg font-extrabold text-slate-800" data-id="element-255">
                          Gesamtbetrag
                        </span>
                        <span className="text-lg font-extrabold text-[#3b7780]" data-id="element-256">
                          {formatPrice(totalMwst)}
                        </span>
                      </div>
                    </div>

                    <p className="text-xs text-slate-400 font-medium mt-3" data-id="element-257">
                      Inkl. Verpflegung und Abendprogramm
                    </p>
                  </div>

                  {/* Required Checkboxes */}
                  <div className="space-y-4 mt-6" data-id="element-258">
                    <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-xl border-2 transition-all ${formData.agbAccepted ? 'border-[#3b7780] bg-[#3b7780]/5' : errors.agbAccepted ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-white'}`} data-id="element-259">
                      <input type="checkbox" checked={formData.agbAccepted} onChange={e => updateField('agbAccepted', e.target.checked)} className="w-5 h-5 text-[#3b7780] rounded border-slate-300 focus:ring-[#59d0d4] focus:ring-2 cursor-pointer mt-0.5 flex-shrink-0" data-id="element-260" />
                      <span className="text-sm text-slate-600 font-medium leading-relaxed" data-id="element-261">
                        Hiermit bestätige ich, dass ich die{' '}
                        <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#3b7780] font-bold underline hover:text-[#59d0d4]" data-id="element-262">
                          Datenschutzerklärung
                        </a>{' '}
                        und die{' '}
                        <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#3b7780] font-bold underline hover:text-[#59d0d4]" data-id="element-263">
                          AGB
                        </a>{' '}
                        gelesen und verstanden habe. Ich stimme zu, dass meine
                        Angaben und Daten zur Beantwortung meiner Anfrage
                        elektronisch erhoben und gespeichert werden. Die
                        Einwilligung kann jederzeit für die Zukunft widerrufen
                        werden.
                        <span className="text-rose-500" data-id="element-264"> *</span>
                      </span>
                    </label>
                    {errors.agbAccepted && <span className="text-xs text-rose-500 font-medium flex items-center gap-1 ml-1" data-id="element-265">
                        <AlertCircleIcon className="w-3 h-3" data-id="element-266" />
                        {errors.agbAccepted}
                      </span>}

                    <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-xl border-2 transition-all ${formData.anmeldungAccepted ? 'border-[#3b7780] bg-[#3b7780]/5' : errors.anmeldungAccepted ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-white'}`} data-id="element-267">
                      <input type="checkbox" checked={formData.anmeldungAccepted} onChange={e => updateField('anmeldungAccepted', e.target.checked)} className="w-5 h-5 text-[#3b7780] rounded border-slate-300 focus:ring-[#59d0d4] focus:ring-2 cursor-pointer mt-0.5 flex-shrink-0" data-id="element-268" />
                      <span className="text-sm text-slate-600 font-medium leading-relaxed" data-id="element-269">
                        Ich bestätige hiermit die verbindliche Anmeldung zum
                        Fortbildungskurs, gemäß meiner ausgefüllten Daten. Die
                        Rechnung zur Buchung wird nach Bestellung zum
                        nächstmöglichen Zeitpunkt via E-Mail versendet.
                        <span className="text-rose-500" data-id="element-270"> *</span>
                      </span>
                    </label>
                    {errors.anmeldungAccepted && <span className="text-xs text-rose-500 font-medium flex items-center gap-1 ml-1" data-id="element-271">
                        <AlertCircleIcon className="w-3 h-3" data-id="element-272" />
                        {errors.anmeldungAccepted}
                      </span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between z-10 flex-shrink-0" data-id="element-273">
              {step > 1 ? <button type="button" onClick={handlePrev} className="px-6 py-2.5 font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-274">
                  Zurück
                </button> : <div data-id="element-275"></div>}

              {step < 3 ? <button type="button" onClick={handleNext} className="px-8 py-2.5 bg-[#3b7780] hover:bg-[#2d5f66] text-white font-bold rounded-lg transition-all hover:shadow-lg" data-id="element-276">
                  Weiter
                </button> : <button type="button" onClick={() => {
            const newErrors: Record<string, string> = {};
            if (!formData.agbAccepted) newErrors.agbAccepted = 'Bitte bestätigen Sie die Datenschutzerklärung und AGB';
            if (!formData.anmeldungAccepted) newErrors.anmeldungAccepted = 'Bitte bestätigen Sie die verbindliche Anmeldung';
            if (Object.keys(newErrors).length > 0) {
              setErrors(newErrors);
              return;
            }
            console.log('Booking submitted:', formData);
            setIsSubmitted(true);
            setTimeout(() => {
              closeBooking();
            }, 3000);
          }} className="px-8 py-3 bg-[#3b7780] hover:bg-[#2d5f66] text-white font-extrabold rounded-lg transition-all hover:shadow-lg hover:scale-105" data-id="element-277">
                  Zahlungspflichtig anmelden
                </button>}
            </div>
          </>}
      </div>
    </div>, document.body);
}